import {
  EK_PLATFORMS,
  SALES_PLATFORMS,
  type EkPlatform,
  type MissingPriceFilter,
  type MockupTemplateFilter,
  type PhoenixMappingFilter,
  type ReleaseChannelKey,
  type SalesPlatform,
} from '@/api/admin';

/** Delay before a typed filter value is written to the URL. */
export const ADMIN_SEARCH_DEBOUNCE_MS = 300;

/** Page number from a URL param; anything but a positive integer is page 1. */
export function parsePage(value: string | null): number {
  const page = value && /^\d+$/.test(value) ? Number(value) : 1;
  return page >= 1 ? page : 1;
}

// ─── Price table (/admin/products) ────────────────────────────────

/** URL search params of the price table; shareable as a link. */
export const ADMIN_PRODUCT_PARAMS = {
  search: 'search',
  categoryId: 'categoryId',
  manufacturerId: 'manufacturerId',
  isActive: 'isActive',
  missing: 'missing',
  phoenix: 'phoenix',
  mockup: 'mockup',
  channel: 'channel',
  released: 'released',
  platform: 'platform',
  ekPlatform: 'ekPlatform',
  page: 'page',
} as const;

export type AdminProductParamName =
  (typeof ADMIN_PRODUCT_PARAMS)[keyof typeof ADMIN_PRODUCT_PARAMS];

/** Rows per page of the price table (R15). */
export const ADMIN_PRODUCT_PAGE_SIZE = 50;

/** Platform the decoration columns show when the URL names none. */
export const DEFAULT_SALES_PLATFORM: SalesPlatform = 'SELLER_CREATOR';

/** Seller platform the cost column is evaluated for when the URL names none. */
export const DEFAULT_EK_PLATFORM: EkPlatform = 'shirtee';

/** The "missing price" filter options, in the order the select shows them. */
export const MISSING_PRICE_FILTERS: MissingPriceFilter[] = [
  'purchasePrice',
  'recommendedSalesPrice',
  'decorationPrice',
];

/** `true`/`false` from the URL; anything else means "all". */
export function parseIsActive(value: string | null): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

export function parseMissing(
  value: string | null
): MissingPriceFilter | undefined {
  return MISSING_PRICE_FILTERS.find((filter) => filter === value);
}

/** The Phoenix mapping filter options, in the order the select shows them. */
export const PHOENIX_MAPPING_FILTERS: PhoenixMappingFilter[] = [
  'mapped',
  'unmapped',
];

export function parsePhoenix(
  value: string | null
): PhoenixMappingFilter | undefined {
  return PHOENIX_MAPPING_FILTERS.find((filter) => filter === value);
}

/** The mockup template filter options, in the order the select shows them. */
export const MOCKUP_TEMPLATE_FILTERS: MockupTemplateFilter[] = [
  'present',
  'missing',
];

export function parseMockup(
  value: string | null
): MockupTemplateFilter | undefined {
  return MOCKUP_TEMPLATE_FILTERS.find((filter) => filter === value);
}

export function parseSalesPlatform(value: string | null): SalesPlatform {
  return (
    SALES_PLATFORMS.find((platform) => platform === value) ??
    DEFAULT_SALES_PLATFORM
  );
}

export function parseEkPlatform(value: string | null): EkPlatform {
  return (
    EK_PLATFORMS.find((platform) => platform === value) ?? DEFAULT_EK_PLATFORM
  );
}

/**
 * Release channel keys a `channel` URL param may name. Only guards the
 * URL; names and column order come from the summary's `releaseChannels`.
 * Typed as a record so a new key in `ReleaseChannelKey` fails the build.
 */
const RELEASE_CHANNEL_KEYS: Record<ReleaseChannelKey, true> = {
  SHI: true,
  SHICL: true,
  JUSTDROPPIT: true,
};

export function parseReleaseChannel(
  value: string | null
): ReleaseChannelKey | undefined {
  return (Object.keys(RELEASE_CHANNEL_KEYS) as ReleaseChannelKey[]).find(
    (key) => key === value
  );
}
